import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { Send } from "lucide-react";

// ✅ Use environment variables for flexibility
const API_BASE = process.env.REACT_APP_API_BASE;
const SOCKET_URL = process.env.REACT_APP_API_BASE; // same backend server

export default function LiveChat() {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [typing, setTyping] = useState(false);
  const [chatEnded, setChatEnded] = useState(false);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  const userEmail = localStorage.getItem("userEmail");
  const userName = localStorage.getItem("userName") || "User";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ======================================================
     ⚡ Socket.io Setup
  ====================================================== */
  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ["websocket"] });
    socketRef.current = socket;

    // ✅ Join support room
    socket.emit("joinSupportRoom", { email: userEmail });

    // ✅ Listen for messages
    socket.on("support:message", (msg) => {
      if (msg.email === userEmail) setMessages((prev) => [...prev, msg]);
    });

    socket.on("support:ended", () => setChatEnded(true));

    socket.on("typing", (data) => {
      if (data.email !== userEmail) setTyping(true);
      setTimeout(() => setTyping(false), 2000);
    });

    // ✅ Load chat history
    const loadChat = async () => {
      try {
        await axios.post(`${API_BASE}/api/support/start-session`, {
          email: userEmail,
          name: userName,
        });
        const res = await axios.get(`${API_BASE}/api/support/livechat/${userEmail}`);
        setMessages(res.data?.messages || []);
      } catch (err) {
        console.error("Chat load failed:", err);
      }
    };

    loadChat();
    return () => socket.disconnect();
  }, [userEmail]);

  /* ======================================================
     💬 Send Message
  ====================================================== */
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || chatEnded) return;

    const msg = {
      sender: userName,
      email: userEmail,
      text: newMsg.trim(),
      time: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, msg]);
    setNewMsg("");

    socketRef.current.emit("support:message", msg);
    await axios.post(`${API_BASE}/api/support/livechat/send`, msg);
  };

  /* ======================================================
     🛑 End Chat
  ====================================================== */
  const endChat = async () => {
    try {
      await axios.post(`${API_BASE}/api/support/end-session`, { email: userEmail });
      setChatEnded(true);
    } catch (err) {
      console.error("End chat error:", err);
    }
  };

  /* ======================================================
     🖥️ UI
  ====================================================== */
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-neutral-50 to-neutral-100 flex flex-col items-center py-12 px-4">
      
      {/* ===== Title Section ===== */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-neutral-900 via-neutral-700 to-neutral-900 bg-clip-text text-transparent tracking-tight">
          💬 Live Support Chat
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Chat with our team — typically replies within minutes.
        </p>
      </div>

      {/* ===== Chat Box ===== */}
      <div className="bg-white border border-neutral-200 shadow-lg rounded-3xl w-full max-w-2xl flex flex-col overflow-hidden">
        
        {/* ===== Chat Header ===== */}
        <div className="flex justify-end items-center px-5 py-3 border-b border-neutral-200 bg-neutral-50">
          <button
            onClick={endChat}
            className="text-xs text-red-500 hover:text-red-700 font-medium transition"
          >
            End Chat
          </button>
        </div>

        {/* ===== Messages ===== */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center text-neutral-500 mt-10">
              Say hi 👋 — we’re here to help!
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.sender === "Admin" ? "justify-start" : "justify-end"
                }`}
              >
                <div
                  className={`px-4 py-2 rounded-2xl max-w-[70%] text-sm shadow-sm ${
                    m.sender === "Admin"
                      ? "bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-bl-none"
                      : "bg-neutral-900 text-white rounded-br-none"
                  }`}
                >
                  <p>{m.text}</p>
                  <p className="text-[10px] opacity-70 mt-1 text-right">
                    {new Date(m.time || m.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            ))
          )}
          {typing && (
            <p className="text-neutral-500 text-xs italic ml-2">
              Support is typing…
            </p>
          )}
          {chatEnded && (
            <div className="text-center text-xs text-neutral-500 italic mt-4">
              This chat session has been closed.
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* ===== Input Bar ===== */}
        <form
          onSubmit={sendMessage}
          className="border-t border-neutral-200 bg-white p-3 flex gap-2"
        >
          <input
            type="text"
            placeholder={
              chatEnded ? "Chat closed. Please start a new one." : "Type a message..."
            }
            value={newMsg}
            onChange={(e) => {
              setNewMsg(e.target.value);
              socketRef.current.emit("typing", { email: userEmail });
            }}
            disabled={chatEnded}
            className="flex-1 border border-neutral-300 rounded-full px-4 py-2 focus:ring-2 focus:ring-neutral-900 focus:outline-none text-sm disabled:bg-neutral-100"
          />
          <button
            type="submit"
            disabled={chatEnded}
            className={`rounded-full p-2 flex items-center justify-center transition ${
              chatEnded
                ? "bg-neutral-400 cursor-not-allowed"
                : "bg-neutral-900 text-white hover:opacity-90"
            }`}
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* ===== Footer ===== */}
      <p className="text-neutral-500 text-xs mt-6">
        Monday–Friday | 9 AM – 5 PM (Pacific Time)
      </p>
    </div>
  );
}
